<i18n>
{
  "fr": {
    "adult": "{count} Adulte | {count} Adultes",
    "children": "{count} Enfant | {count} Enfants",
    "infant": "{count} Bébé | {count} Bébés",
    "price-details": "Détail du prix",
    "total-luggage": "Bagages",
    "travellers": "Les participants :",
    "tax-free-flight": "Vol hors taxes",
    "taxes": "Taxes",
    "administrative-fees": "Frais de dossier",
    "total-tax-included": "Total TTC",
    "total-amount-excluding-grant": "Montant total (hors subvention)",
    "terms-and-conditions": {
      "1": "J'ai pris connaissance des",
      "1-link": "Informations Légales",
      "2": "J'accepte sans réserve les",
      "2-link": "Conditions Générales de Vente et les Conditions Tarifaires"
    },
    "validate": "Valider",
    "conditions": "Conditions tarifaires",
    "conditions-warning": "(Les conditions tarifaires vous sont données à titre indicatif. Elles peuvent être incomplètes et modifiées sans préavis par la compagnie aérienne.)"
  },
  "en": {
    "adult": "{count} Adult | {count} Adults",
    "children": "{count} Children | {count} Childrens",
    "infant": "{count} Infant | {count} Infants",
    "price-details": "Price details",
    "total-luggage": "Luggage",
    "travellers": "Travellers :",
    "tax-free-flight": "Tax free flight",
    "taxes": "Fees",
    "administrative-fees": "Administrative fees",
    "total-tax-included": "Total tax included",
    "total-amount-excluding-grant": "Total amount (excluding subsidy)",
    "terms-and-conditions": {
      "1": "I have read the ",
      "1-link": "Legal Information",
      "2": "I agreed to the ",
      "2-link": "General terms and conditions of sale"
    },
    "validate": "Validate",
    "conditions": "Terms and conditions",
    "conditions-warning": "(The fare conditions are given as an indication. They may be incomplete and modified without notice by the airline.)"
  }
}
</i18n>

<template>
  <v-card
    flat
  >
    <v-card-title>
      <h1 class="text--primary title">
        {{ $t('price-details') }}
      </h1>
    </v-card-title>

    <h4 class="grey--text text--darken-3 ml-7 mb-1">
      {{ $t('travellers') }}
    </h4>

    <v-card-text>
      <v-row dense>
        <v-col
          v-if="priceDetailsPerTypeOfPassenger.adultAmount > 0"
          cols="4"
        >
          <v-card
            outlined
            class="ml-3"
          >
            <v-card-title>{{ $tc('adult', priceDetailsPerTypeOfPassenger.adultAmount) }}</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col cols="7">
                  <div>{{ $t('tax-free-flight') }}</div>
                  <div>{{ $t('taxes') }}</div>
                  <div>{{ $t('administrative-fees') }}</div>
                  <div v-if="itinerary.price.luggage_amount">
                    {{ $t('total-luggage') }}
                  </div>
                  <div>{{ $t('total-tax-included') }}</div>
                </v-col>
                <v-col>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.adultAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.adultPrice, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.adultAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.adultTaxes, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.adultAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.handlingFee, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div
                    v-if="itinerary.price.luggage_amount"
                    class="text-right"
                  >
                    {{ totalLuggagePrice }}
                  </div>
                  <div class="text-right">
                    {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.adultTotal, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          v-if="priceDetailsPerTypeOfPassenger.childrenAmount > 0"
          cols="4"
        >
          <v-card outlined>
            <v-card-title>{{ $tc('children', priceDetailsPerTypeOfPassenger.childrenAmount) }}</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col cols="7">
                  <div>{{ $t('tax-free-flight') }}</div>
                  <div>{{ $t('taxes') }}</div>
                  <div>{{ $t('administrative-fees') }}</div>
                  <div>{{ $t('total-tax-included') }}</div>
                </v-col>
                <v-col>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.childrenAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.childrenPrice, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.childrenAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.childrenTaxes, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.childrenAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.handlingFee, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.childrenTotal, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          v-if="priceDetailsPerTypeOfPassenger.infantAmount > 0"
          cols="4"
        >
          <v-card outlined>
            <v-card-title>{{ $tc('infant', priceDetailsPerTypeOfPassenger.infantAmount) }}</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col cols="7">
                  <div>{{ $t('tax-free-flight') }}</div>
                  <div>{{ $t('taxes') }}</div>
                  <div>{{ $t('administrative-fees') }}</div>
                  <div>{{ $t('total-tax-included') }}</div>
                </v-col>
                <v-col>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.infantAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.infantPrice, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.infantAmount }} x {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.infantTaxes, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                  <div class="text-right">
                    {{ priceDetailsPerTypeOfPassenger.infantAmount }} x
                    {{ formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.handlingFee, priceDetailsPerTypeOfPassenger.currency) }}
                  </div>
                  <div class="text-right">
                    {{
                      formatPriceWithNarrowSymbol(priceDetailsPerTypeOfPassenger.infantTotal, priceDetailsPerTypeOfPassenger.currency)
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row
        v-if="fareRules && fareRules.length"
        class="mt-3 mb-5"
      >
        <v-col cols="12">
          <h4 class="grey--text text--darken-3 ml-3 mb-1">
            {{ $t('conditions') }} <span class="caption">{{ $t('conditions-warning') }}</span>
          </h4>
          <ul>
            <li
              v-for="(rule, index) in fareRules"
              :key="index"
              class="caption"
            >
              <v-row no-gutters>
                <v-col cols="12">
                  {{ $i18n.locale === 'en' ? rule.en : rule.fr }}
                </v-col>
              </v-row>
            </li>
          </ul>
        </v-col>
      </v-row>

      <h2 class="mt-4 text-right">
        {{ $t('total-amount-excluding-grant') }}: <span id="total-price">{{ totalPrice }}</span>
      </h2>

      <v-row>
        <v-spacer />
        <v-checkbox v-model="canSubmit">
          <template #label>
            <div>
              {{ $t('terms-and-conditions.1') }}
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <router-link
                    to="/about_us"
                    @click.stop.prevent="openInformations"
                    v-on="on"
                  >
                    {{ $t('terms-and-conditions.1-link') }}
                  </router-link>
                </template>
                Opens in new window
              </v-tooltip>
              .

              {{ $t('terms-and-conditions.2') }}
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <router-link
                    to="/general_terms_and_conditions"
                    @click.stop.prevent="openTermsAndConditions"
                    v-on="on"
                  >
                    {{ $t('terms-and-conditions.2-link') }}
                  </router-link>
                </template>
                Opens in new window
              </v-tooltip>
              .
            </div>
          </template>
        </v-checkbox>
        <v-spacer />
      </v-row>

      <v-row>
        <v-spacer />
        <v-btn
          :disabled="!canSubmit"
          color="primary"
          min-width="300px"
          @click="$emit('submit')"
        >
          {{ $t('validate') }}
        </v-btn>
        <v-spacer />
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import globalMixin from '@/mixins/global'

export default {
  name: 'FlightPriceSummary',
  mixins: [globalMixin],
  data: () => ({
    canSubmit: false
  }),
  computed: {
    ...mapState({
      fareRules: state => state.fb.searchResults.selectedItinerary.detail_fare_rules,
      itinerary: state => state.fb.searchResults.updatedSelectedItinerary
    }),
    /**
     * Return the total price of the itinerary based on the rawPrice and the currency the backend send us
     * @return {string}
     */
    totalPrice () {
      const rawPrice = this.itinerary.price.total
      const currency = this.itinerary.price.currency
      return this.formatPriceWithCode(rawPrice, currency)
    },

    totalLuggagePrice () {
      const rawPrice = this.itinerary.price.luggage_amount
      const currency = this.itinerary.price.currency
      return this.formatPriceWithNarrowSymbol(rawPrice, currency)
    },
    /**
     * Return a huge object with the price details for each category of traveller (ADT, CNN, INF)
     * @return {{childrenPrice: number, childrenTaxes: number, handlingFee: number, adultTaxes: number, adultPrice: number, infantTaxes: number, infantPrice: number, childrenAmount: number, adultTotal: number, infantAmount: number, childrenTotal: number, currency: string, infantTotal: number, adultAmount: number}}
     */
    priceDetailsPerTypeOfPassenger () {
      // console.log('Price summary this.itinerary.price : ', this.itinerary.price)
      const priceDetails = this.itinerary.price

      let adultAmount = 0
      let adultPrice = 0
      let adultTaxes = 0
      let adultTotal = 0
      let childrenAmount = 0
      let childrenPrice = 0
      let childrenTaxes = 0
      let childrenTotal = 0
      let infantAmount = 0
      let infantPrice = 0
      let infantTaxes = 0
      let infantTotal = 0
      const currency = priceDetails.currency

      // console.log('SJIJSIJSIJSI', priceDetails.passengers_prices)

      let price
      for (price of priceDetails.passengers_prices) {
        if (price.code === 'ADT') {
          adultAmount += price.quantity
          adultPrice = price.total
          // adultTaxes = price.displayed_amount_taxes
          // adultTotal += (price.total + price.displayed_amount_taxes) * price.quantity
          adultTaxes = price.amount_taxes
          adultTotal += (price.total + price.amount_taxes) * price.quantity
        } else if (price.code === 'CNN') {
          childrenAmount += price.quantity
          childrenPrice = price.total
          // childrenTaxes = price.displayed_amount_taxes
          // childrenTotal += (price.total + price.displayed_amount_taxes) * price.quantity
          childrenTaxes = price.amount_taxes
          childrenTotal += (price.total + price.amount_taxes) * price.quantity
        } else if (price.code === 'INF') {
          infantAmount += price.quantity
          infantPrice = price.total
          // infantTaxes = price.displayed_amount_taxes
          // infantTotal += (price.total + price.displayed_amount_taxes) * price.quantity
          infantTaxes = price.amount_taxes
          infantTotal += (price.total + price.amount_taxes) * price.quantity
        }
      }

      // Handling fee must be computed because backend return us the total of all the handling fee and not the detail
      // const handlingFee = priceDetails.dossier / (adultAmount + childrenAmount)
      const handlingFee = priceDetails.dossier / (adultAmount + childrenAmount + infantAmount)
      adultTotal += (adultAmount * handlingFee)
      childrenTotal += (childrenAmount * handlingFee)
      infantTotal += (infantAmount * handlingFee)

      // add total luggage amount to adultTotal
      adultTotal += this.itinerary.price.luggage_amount

      return {
        adultAmount: adultAmount,
        adultPrice: adultPrice,
        adultTaxes: adultTaxes,
        adultTotal: adultTotal,
        childrenAmount: childrenAmount,
        childrenPrice: childrenPrice,
        childrenTaxes: childrenTaxes,
        childrenTotal: childrenTotal,
        infantAmount: infantAmount,
        infantPrice: infantPrice,
        infantTaxes: infantTaxes,
        infantTotal: infantTotal,
        handlingFee: handlingFee,
        currency: currency
      }
    }
  }
}
</script>

<style scoped>
#total-price {
  color: #0089ba;
}
</style>
